/**
 * Prepare a Notice of Commencement manually — for owners who have the property/owner
 * facts in hand and need the sign-ready form WITHOUT an associated BEAM job (e.g. a
 * permit pulled outside a job). Owner/office only (gated server-side; a 403 surfaces
 * as an error here).
 *
 * LEGAL / SAFETY (invariant 14): prepare-to-attest. This generates a DRAFT PDF only.
 * BEAM never notarizes, files or records. The OWNER notarizes and records it with the
 * county clerk and posts a copy on the job site. NEVER imports @smartbox/* (mobile
 * invariant 1). No raw style values.
 */
import { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { prepareNoc, type PrepareNocRequest, type GeneratedNoc } from '../../../src/api/permits';
import { Screen, Text, Card, Button, Field } from '../../../src/components/ui';
import { theme } from '../../../src/theme';
import { extractErrorMessage } from '../../../src/lib/format';

export default function PrepareNocScreen() {
  const router = useRouter();
  const queryClient = useQueryClient();

  const [ownerName, setOwnerName] = useState('');
  const [ownerAddress, setOwnerAddress] = useState('');
  const [legalDescription, setLegalDescription] = useState('');
  const [improvements, setImprovements] = useState('');
  const [taxFolio, setTaxFolio] = useState('');
  const [contractValue, setContractValue] = useState('');
  const [contractorName, setContractorName] = useState('');
  const [permitNumber, setPermitNumber] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GeneratedNoc | null>(null);

  const mutation = useMutation({
    mutationFn: (body: PrepareNocRequest) => prepareNoc(body),
    onSuccess: (data) => {
      setResult(data);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['nocs'] });
    },
    onError: (err) => setError(extractErrorMessage(err, 'Could not prepare the Notice of Commencement.')),
  });

  const onSubmit = () => {
    setError(null);
    if (!ownerName.trim()) return setError('Enter the owner / lessee name.');
    const body: PrepareNocRequest = { owner_name: ownerName.trim() };
    if (ownerAddress.trim()) body.owner_address = ownerAddress.trim();
    if (legalDescription.trim()) body.property_legal_description = legalDescription.trim();
    if (improvements.trim()) body.general_description = improvements.trim();
    if (taxFolio.trim()) body.tax_folio_number = taxFolio.trim();
    if (contractorName.trim()) body.contractor_name = contractorName.trim();
    if (permitNumber.trim()) body.permit_number = permitNumber.trim();
    const value = Number.parseFloat(contractValue);
    if (contractValue.trim() && Number.isFinite(value)) body.contract_value = value;
    mutation.mutate(body);
  };

  if (result) {
    const req = result.nocRequired;
    return (
      <Screen scroll>
        <Text variant="title">NOC prepared</Text>
        <Card>
          <Text variant="bodyStrong">Draft generated</Text>
          <Text variant="small" color="secondary">
            A sign-ready Notice of Commencement PDF was generated for {result.noc.owner_name}.
            It is a DRAFT — BEAM has not notarized, filed or recorded it. Download it from the
            Permits list, then sign/notarize and record it with the Nassau County Clerk and post
            a copy on the job site before the first inspection.
          </Text>
        </Card>
        <Card>
          <Text variant="label" color="secondary">NOC required (form thresholds)</Text>
          <Text variant="bodyStrong" color={req.required ? 'danger' : 'primary'}>
            {req.required ? 'YES' : 'NO'}{req.needsHumanReview ? ' — needs human review' : ''}
          </Text>
          <Text variant="small" color="secondary">{req.reason}</Text>
        </Card>
        <Button title="Done" onPress={() => router.replace('/(app)/permits')} />
        <Button
          title="Prepare another"
          variant="secondary"
          onPress={() => {
            setResult(null);
            setOwnerName('');
            setOwnerAddress('');
            setLegalDescription('');
            setImprovements('');
            setTaxFolio('');
            setContractValue('');
            setContractorName('');
            setPermitNumber('');
          }}
        />
      </Screen>
    );
  }

  return (
    <Screen scroll>
      <Text variant="title">Prepare a Notice of Commencement</Text>
      <Card>
        <Text variant="small" color="secondary">
          Enter the owner and property details to generate a sign-ready NOC PDF. BEAM PREPARES the
          form only — it never notarizes, files or records. You notarize and record it with the
          county clerk and post a copy on the job site yourself.
        </Text>
      </Card>

      <Card>
        <Text variant="bodyStrong">Owner / property</Text>
        <Field label="Owner / lessee name" placeholder="e.g. Jane Homeowner" value={ownerName} onChangeText={setOwnerName} accessibilityLabel="Owner or lessee name" />
        <Field label="Property / owner address" placeholder="123 Main St, Fernandina Beach, FL 32034" value={ownerAddress} onChangeText={setOwnerAddress} accessibilityLabel="Property or owner address" />
        <Field label="Legal description of property" placeholder="Lot / Block / Subdivision or metes & bounds" value={legalDescription} onChangeText={setLegalDescription} multiline accessibilityLabel="Legal description of property" />
        <Field label="Tax folio / parcel number (optional)" placeholder="e.g. 00-00-00-0000-0000-0000" value={taxFolio} onChangeText={setTaxFolio} autoCapitalize="characters" autoCorrect={false} accessibilityLabel="Tax folio number" />
      </Card>

      <Card>
        <Text variant="bodyStrong">Improvements & contract</Text>
        <Field label="General description of improvements" placeholder="e.g. Re-roof single-family residence" value={improvements} onChangeText={setImprovements} multiline accessibilityLabel="General description of improvements" />
        <Field label="Contract value (optional)" placeholder="0.00" value={contractValue} onChangeText={setContractValue} keyboardType="decimal-pad" accessibilityLabel="Contract value" />
        <Text variant="small" color="secondary">
          Used to flag whether a NOC is required (over $5,000 general / $15,000 mechanical, per the
          Nassau / Fernandina Beach form).
        </Text>
        <Field label="Contractor name (optional)" placeholder="Defaults to your business name" value={contractorName} onChangeText={setContractorName} accessibilityLabel="Contractor name" />
        <Field label="Permit number (optional)" placeholder="If already issued" value={permitNumber} onChangeText={setPermitNumber} autoCapitalize="characters" autoCorrect={false} accessibilityLabel="Permit number" />
      </Card>

      {error ? (
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">{error}</Text>
        </Card>
      ) : null}

      <View style={styles.actions}>
        <Button title="Generate NOC draft" onPress={onSubmit} loading={mutation.isPending} disabled={mutation.isPending} />
        <Button title="Cancel" variant="secondary" onPress={() => router.back()} disabled={mutation.isPending} />
      </View>
    </Screen>
  );
}

const styles = StyleSheet.create({
  actions: { gap: theme.spacing.sm, marginTop: theme.spacing.md },
});
